import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 10;
import { statsApi, type NinmunStatus } from '../features/stats/api.ts';
import { downloadAuthed, meetingsApi } from '../features/meetings/api.ts';
import { yaumiyyaApi } from '../features/yaumiyya/api.ts';
import { termsApi } from '../features/terms/api.ts';
import { dv } from '../i18n/dv.ts';
import { formatDvDateShort, meetingNumber } from '../lib/dvDate.ts';
import { type Body, bodyOfRole } from '../lib/body.ts';
import { BodyToggle } from '../features/orgSettings/BodyToggle.tsx';
import { useAuthStore } from '../store/auth.ts';

// Compact icon-button to download a final document PDF (authenticated fetch →
// blob → save). Rendered inline next to the relevant status, so its column
// makes clear which document it is; the aria-label/title disambiguates too.
function DocDownload({ url, fileName, label }: { url: string; fileName: string; label: string }): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void downloadAuthed(url, fileName).finally(() => setBusy(false));
      }}
      className="grid size-8 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-[hsl(var(--surface-high))] hover:text-foreground disabled:opacity-50"
    >
      <Icon name="download" size={18} />
    </button>
  );
}

// Pill variant for the yaumiyya status (matches the rest of the app).
const YAUMIYYA_PILL: Record<string, string> = {
  DRAFT: 'pill pill-neutral',
  PENDING_APPROVAL: 'pill pill-pending',
  APPROVED: 'pill pill-success',
};

const NINMUN_PILL: Record<NinmunStatus, string> = {
  SIGNED: 'pill pill-success',
  SIGNING: 'pill pill-pending',
  GENERATED: 'pill pill-neutral',
  NONE: 'pill pill-neutral',
};

// «ބައްދަލުވުމުގެ ހުލާސާ» — every meeting with agenda-sent date + yaumiyya +
// ninmun status, plus a small KPI strip at the top.
export function StatsSummary(): JSX.Element {
  // Default to the viewer's own body when they're elected, else COUNCIL.
  const role = useAuthStore((s) => s.user?.role);
  const [body, setBody] = useState<Body>(() => (role ? bodyOfRole(role) : null) ?? 'COUNCIL');

  // Term (dhauru) filter — '' = all terms; defaults to the body's active term.
  const termsQ = useQuery({ queryKey: ['terms', body], queryFn: () => termsApi.list(body) });
  const terms = useMemo(() => termsQ.data ?? [], [termsQ.data]);
  const [termId, setTermId] = useState<string>('');
  useEffect(() => {
    if (!terms.length) { setTermId(''); return; }
    const active = terms.find((t) => t.isActive);
    setTermId(active ? active.id : '');
  }, [terms]);

  const meetingsQ = useQuery({
    queryKey: ['stats', 'meetings', body, termId],
    queryFn: () => statsApi.meetings(body, termId || undefined),
  });
  const meetings = meetingsQ.data;
  const meetingsEmpty = !meetings || meetings.length === 0;

  // Free-text search over the meeting list — title or meeting number.
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const filtered = (meetings ?? []).filter((m) =>
    needle === '' ||
    m.title.toLowerCase().includes(needle) ||
    meetingNumber(m.year, m.sequence).toLowerCase().includes(needle),
  );

  // Client-side pagination over the (bounded, filtered) meeting list.
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [body, termId, needle]);
  const pagedMeetings = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const kpis = meetings
    ? {
        total: meetings.length,
        yaumiyyaDone: meetings.filter((m) => m.yaumiyya?.status === 'APPROVED').length,
        ninmunSigned: meetings.filter((m) => m.ninmunStatus === 'SIGNED').length,
      }
    : null;

  return (
    <div className="space-y-6">
      {/* Toolbar — filters on one side, search on the other. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-3xl bg-[hsl(var(--chrome))] p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <BodyToggle body={body} onChange={setBody} />
          {terms.length > 0 && (
            <select
              aria-label={dv.meetings.term}
              value={termId}
              onChange={(e) => setTermId(e.target.value)}
              className="select w-auto rounded-full py-1.5"
            >
              {terms.map((t) => (
                <option key={t.id} value={t.id}>{t.number} ވަނަ ދައުރު</option>
              ))}
              <option value="">{dv.meetings.allTerms}</option>
            </select>
          )}
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={dv.meetings.search}
            className="input w-full rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: dv.stats.kpiMeetings, value: kpis.total, icon: 'event' },
            { label: dv.stats.kpiYaumiyya, value: kpis.yaumiyyaDone, icon: 'description' },
            { label: dv.stats.kpiNinmun, value: kpis.ninmunSigned, icon: 'verified' },
          ].map((k) => (
            <div key={k.label} className="flex flex-col items-center gap-1.5 rounded-3xl bg-[hsl(var(--chrome))] p-4 text-center sm:p-5">
              <span className="grid size-10 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                <Icon name={k.icon} size={20} />
              </span>
              <span className="tabular text-2xl font-bold text-foreground sm:text-3xl" dir="ltr">{k.value}</span>
              <span className="text-[11px] leading-tight text-muted-foreground sm:text-xs">{k.label}</span>
            </div>
          ))}
        </div>
      )}

      <section className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))]">
        <h2 className="status-label border-b border-black/[0.05] px-4 py-3 text-muted-foreground sm:px-5">
          {dv.stats.meetingsHeading}
        </h2>
        {meetingsQ.isLoading ? (
          <p className="p-6 text-sm text-muted-foreground" role="status" aria-live="polite">
            {dv.common.loading}
          </p>
        ) : meetingsEmpty ? (
          <p className="p-8 text-center text-sm text-muted-foreground">{dv.meetings.empty}</p>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">{dv.meetings.searchEmpty}</p>
        ) : (
          <>
            {/* Mobile: stacked cards */}
            <ul className="divide-y divide-black/[0.05] sm:hidden">
              {pagedMeetings.map((m) => (
                <li key={m.id} className="space-y-2.5 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to="/meetings/$meetingId"
                      params={{ meetingId: m.id }}
                      className="font-medium leading-snug hover:underline"
                    >
                      {m.title}
                    </Link>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div>
                      <dt className="text-muted-foreground">{dv.stats.date}</dt>
                      <dd>{formatDvDateShort(m.scheduledAt)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{dv.stats.agendaSent}</dt>
                      <dd className="inline-flex items-center gap-2">
                        {m.agendaPassedAt ? formatDvDateShort(m.agendaPassedAt) : '—'}
                        {m.agendaDocId && (
                          <DocDownload url={meetingsApi.documentUrl(m.agendaDocId)} fileName={`agenda-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadAgenda} />
                        )}
                      </dd>
                    </div>
                  </dl>
                  <div className="flex flex-wrap items-center gap-2 pt-0.5">
                    <span className="text-xs text-muted-foreground">{dv.stats.yaumiyya}:</span>
                    {m.yaumiyya ? (
                      <span className={YAUMIYYA_PILL[m.yaumiyya.status] ?? 'pill pill-neutral'}>
                        {dv.yaumiyya.status[m.yaumiyya.status]}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    {m.yaumiyyaDocId && (
                      <DocDownload url={yaumiyyaApi.documentUrl(m.yaumiyyaDocId)} fileName={`yaumiyya-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadYaumiyya} />
                    )}
                    <span className="ms-2 text-xs text-muted-foreground">{dv.stats.ninmun}:</span>
                    {m.ninmunStatus === 'NONE' ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className={NINMUN_PILL[m.ninmunStatus]}>
                        {dv.stats.ninmunStatus[m.ninmunStatus]}
                      </span>
                    )}
                    {m.ninmunDocId && (
                      <DocDownload url={yaumiyyaApi.ninmunDocumentUrl(m.ninmunDocId)} fileName={`ninmun-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadNinmun} />
                    )}
                    <span className="ms-2 text-xs text-muted-foreground">{dv.stats.haaziree}:</span>
                    {m.haazireeDocId ? (
                      <DocDownload url={yaumiyyaApi.haazireeDocumentUrl(m.haazireeDocId)} fileName={`haaziree-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadHaaziree} />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: table */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-black/[0.05] text-xs text-muted-foreground">
                    <th className="px-4 py-2.5 text-start font-medium sm:px-5">{dv.stats.meeting}</th>
                    <th className="px-4 py-2.5 text-start font-medium">{dv.stats.date}</th>
                    <th className="px-4 py-2.5 text-start font-medium">{dv.stats.agenda}</th>
                    <th className="px-4 py-2.5 text-start font-medium">{dv.stats.yaumiyya}</th>
                    <th className="px-4 py-2.5 text-start font-medium">{dv.stats.ninmun}</th>
                    <th className="px-4 py-2.5 text-start font-medium">{dv.stats.haaziree}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/[0.05]">
                  {pagedMeetings.map((m) => (
                    <tr key={m.id} className="transition-colors hover:bg-[hsl(var(--surface-high))]">
                      <td className="px-4 py-3 text-start sm:px-5">
                        <Link
                          to="/meetings/$meetingId"
                          params={{ meetingId: m.id }}
                          className="font-medium hover:underline"
                        >
                          {m.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-start text-muted-foreground">{formatDvDateShort(m.scheduledAt)}</td>
                      <td className="px-4 py-3 text-start whitespace-nowrap">
                        {m.agendaDocId ? (
                          <DocDownload url={meetingsApi.documentUrl(m.agendaDocId)} fileName={`agenda-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadAgenda} />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-start whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          {m.yaumiyya ? (
                            <span className={YAUMIYYA_PILL[m.yaumiyya.status] ?? 'pill pill-neutral'}>
                              {dv.yaumiyya.status[m.yaumiyya.status]}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                          {m.yaumiyyaDocId && (
                            <DocDownload url={yaumiyyaApi.documentUrl(m.yaumiyyaDocId)} fileName={`yaumiyya-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadYaumiyya} />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-start whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          {m.ninmunStatus === 'NONE' ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span className={NINMUN_PILL[m.ninmunStatus]}>
                              {dv.stats.ninmunStatus[m.ninmunStatus]}
                            </span>
                          )}
                          {m.ninmunDocId && (
                            <DocDownload url={yaumiyyaApi.ninmunDocumentUrl(m.ninmunDocId)} fileName={`ninmun-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadNinmun} />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-start whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          {m.haazireeDocId ? (
                            <DocDownload url={yaumiyyaApi.haazireeDocumentUrl(m.haazireeDocId)} fileName={`haaziree-${m.year}-${m.sequence}.pdf`} label={dv.stats.downloadHaaziree} />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onChange={setPage} />
    </div>
  );
}
